import { useState, useEffect, useMemo, useCallback } from "react"
import toast from "react-hot-toast"
import { useAuth } from "../hooks/useAuth"
import { MilestoneTodoChecklist } from "./MilestoneTodoChecklist"
import {
  useMilestones,
  Milestone,
  MilestoneStatus,
  STATUS_META,
  addDaysIso,
  diffDays,
  formatDate,
  getTodoProgress,
  getAssignees,
  isAssignedTo,
} from "../hooks/useMilestones"

// 내 할 일에서 다룰 상태 (Completed / Archive 는 기본 제외)
const ACTIVE_STATUSES: MilestoneStatus[] = ["todo", "in_progress", "review", "stuck"]

// 빠른 상태 변경 시 제공할 다음 단계
const NEXT_STATUS: MilestoneStatus[] = ["todo", "in_progress", "review", "completed", "stuck"]

type StatusFilter = "all" | MilestoneStatus

/** 오늘 기준 D-day 계산 (양수: 남은 일수, 0: 오늘, 음수: 초과) */
function getDday(dueDate: string): number {
  const today = new Date().toISOString().slice(0, 10)
  return diffDays(today, dueDate)
}

/** D-day 배지 스타일/문구 */
function DdayBadge({ milestone }: { milestone: Milestone }) {
  if (milestone.status === "completed") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-700">
        완료
      </span>
    )
  }

  const d = getDday(milestone.dueDate)
  const slack = Number(milestone.slackDays || 0)
  const slackDeadline = slack > 0 ? addDaysIso(milestone.dueDate, slack) : ""
  const slackLeft = slackDeadline ? getDday(slackDeadline) : 0

  if (d < 0) {
    // 목표일 초과 → Slack 여유가 남아 있으면 완화된 경고
    if (slack > 0 && slackLeft >= 0) {
      return (
        <span
          title={`Slack 포함 최종 기한 ${formatDate(slackDeadline)}`}
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700"
        >
          Slack {slackLeft}일
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">
        {Math.abs(d)}일 지연
      </span>
    )
  }
  if (d === 0) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">
        오늘 마감
      </span>
    )
  }
  if (d <= 7) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700">
        D-{d}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-slate-100 text-slate-600">
      D-{d}
    </span>
  )
}

export function MyTodoList() {
  const auth = useAuth()
  const { milestones, loading, fetchMilestones, updateMilestone, toggleTodo } = useMilestones()

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
  const [includeDone, setIncludeDone] = useState(false)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [todoSavingId, setTodoSavingId] = useState<string | null>(null)

  useEffect(() => {
    fetchMilestones()
  }, [fetchMilestones])

  const myUserId = auth?.user?.userId || ""
  const myName = auth?.userName || ""

  // 나에게 지정된 마일스톤만 추출 (복수 담당자 assignees 배열 기준, 레거시 단일 담당자도 호환)
  const mine = useMemo(() => {
    if (!myUserId && !myName) return []
    return milestones.filter((m) => isAssignedTo(m, myUserId, myName))
  }, [milestones, myUserId, myName])

  // 상태 필터 + 완료 포함 여부 적용 후 마감일 오름차순 정렬
  const visible = useMemo(() => {
    let list = mine
    if (!includeDone) {
      list = list.filter((m) => m.status !== "completed" && m.status !== "archive")
    }
    if (statusFilter !== "all") {
      list = list.filter((m) => m.status === statusFilter)
    }
    return [...list].sort((a, b) => a.dueDate.slice(0, 10).localeCompare(b.dueDate.slice(0, 10)))
  }, [mine, includeDone, statusFilter])

  // 상태별 카운트 (필터 탭 배지용)
  const counts = useMemo(() => {
    const base = includeDone ? mine : mine.filter((m) => m.status !== "completed" && m.status !== "archive")
    const map: Record<string, number> = { all: base.length }
    ACTIVE_STATUSES.forEach((s) => {
      map[s] = base.filter((m) => m.status === s).length
    })
    map.completed = mine.filter((m) => m.status === "completed").length
    return map
  }, [mine, includeDone])

  // 요약 지표
  const summary = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10)
    const active = mine.filter((m) => m.status !== "completed" && m.status !== "archive")
    const overdue = active.filter((m) => m.dueDate.slice(0, 10) < today).length
    const soonLimit = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10)
    const dueSoon = active.filter(
      (m) => m.dueDate.slice(0, 10) >= today && m.dueDate.slice(0, 10) <= soonLimit
    ).length
    return {
      total: active.length,
      todo: active.filter((m) => m.status === "todo").length,
      overdue,
      dueSoon,
    }
  }, [mine])

  // 담당자 본인이 자신의 마일스톤 상태를 변경
  const handleStatusChange = useCallback(
    async (m: Milestone, status: MilestoneStatus) => {
      if (status === m.status) return
      setSavingId(m._id)
      try {
        await updateMilestone(m._id, {
          status,
          completedAt: status === "completed" ? new Date().toISOString() : undefined,
        })
        await fetchMilestones()
        toast.success(`"${m.title}" → ${STATUS_META[status].label}`)
      } catch (error) {
        console.error("상태 변경 실패:", error)
        toast.error("상태 변경에 실패했습니다.")
      } finally {
        setSavingId(null)
      }
    },
    [updateMilestone, fetchMilestones]
  )

  // 세부 To do 체크 토글 (낙관적 반영 + 즉시 저장)
  const handleToggleTodo = useCallback(
    async (m: Milestone, todoId: string) => {
      setTodoSavingId(m._id)
      try {
        await toggleTodo(m, todoId, myName)
      } catch (error) {
        console.error("To do 저장 실패:", error)
        toast.error("To do 저장에 실패했습니다.")
      } finally {
        setTodoSavingId(null)
      }
    },
    [toggleTodo, myName]
  )

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">To do 목록</h1>
        <p className="mt-1 text-sm text-gray-600">
          {myName ? `${myName}님에게` : "나에게"} 지정된 각 프로젝트의 마일스톤을 한 곳에서 확인합니다.
        </p>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 mb-1">내 진행 항목</p>
          <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 mb-1">작업필요</p>
          <p className="text-2xl font-bold text-gray-700">{summary.todo}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 mb-1">7일 내 임박</p>
          <p className="text-2xl font-bold text-amber-600">{summary.dueSoon}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 mb-1">지연</p>
          <p className="text-2xl font-bold text-red-600">{summary.overdue}</p>
        </div>
      </div>

      {/* 필터 바 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                statusFilter === "all"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              전체 <span className="ml-1 opacity-80">{counts.all || 0}</span>
            </button>
            {ACTIVE_STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  statusFilter === s
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${STATUS_META[s].dot}`} />
                {STATUS_META[s].label}
                <span className="opacity-80">{counts[s] || 0}</span>
              </button>
            ))}
          </div>

          <div className="flex-1" />

          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={includeDone}
              onChange={(e) => {
                setIncludeDone(e.target.checked)
                setStatusFilter("all")
              }}
              className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            완료·보관 포함
          </label>
        </div>
      </div>

      {/* 본문 */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 py-20 text-center">
          <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">내 할 일을 불러오는 중...</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
            <svg className="w-7 h-7 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>
          <p className="text-base font-semibold text-gray-900 mb-1">지정된 마일스톤이 없습니다</p>
          <p className="text-sm text-gray-500">
            일정관리 화면에서 마일스톤 담당자로 지정되면 이곳에 모아서 표시됩니다.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((m) => {
            const meta = STATUS_META[m.status]
            const slack = Number(m.slackDays || 0)
            const todos = m.todos || []
            const todoTotal = todos.length
            const todoDone = todos.filter((t) => t?.done).length
            const todoPercent = getTodoProgress(todos)
            const assignees = getAssignees(m)
            const coWorkers = assignees.filter((a) =>
              a.userId ? a.userId !== myUserId : a.userName !== myName
            )
            return (
              <div
                key={m._id}
                className={`bg-white rounded-xl border-l-4 ${meta.border} border-t border-r border-b border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {/* 좌측: 내용 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${meta.chip}`}>
                        {meta.label}
                      </span>
                      {m.projectName ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                          {m.projectName}
                        </span>
                      ) : null}
                      <DdayBadge milestone={m} />
                    </div>

                    <h3 className="text-base font-bold text-gray-900 break-words">{m.title}</h3>

                    {m.deliverable ? (
                      <div className="mt-2 flex items-start gap-2">
                        <span className="mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold bg-gray-100 text-gray-600 shrink-0">
                          산출물
                        </span>
                        <p className="text-sm text-gray-700 break-words">{m.deliverable}</p>
                      </div>
                    ) : null}

                    {m.description ? (
                      <p className="mt-2 text-sm text-gray-500 break-words">{m.description}</p>
                    ) : null}

                    {todoTotal > 0 ? (
                      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <span className="text-xs font-bold text-gray-700">
                            세부 To do
                            <span className="ml-1.5 font-semibold text-orange-600">
                              {todoDone}/{todoTotal}
                            </span>
                          </span>
                          <div className="flex items-center gap-2">
                            {todoSavingId === m._id ? (
                              <span className="text-[11px] text-orange-600">저장 중...</span>
                            ) : null}
                            <div className="h-1.5 w-24 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-orange-500 rounded-full transition-all"
                                style={{ width: `${todoPercent ?? 0}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        <MilestoneTodoChecklist
                          todos={todos}
                          saving={todoSavingId === m._id}
                          onToggle={(todoId) => handleToggleTodo(m, todoId)}
                        />
                      </div>
                    ) : null}

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span>
                        기간 {formatDate(m.startDate)} ~ {formatDate(m.dueDate)}
                      </span>
                      {slack > 0 ? <span>Slack {slack}일</span> : null}
                      {typeof m.progress === "number" && m.progress > 0 ? (
                        <span>진행률 {m.progress}%</span>
                      ) : null}
                      {coWorkers.length > 0 ? (
                        <span>함께 {coWorkers.map((a) => a.userName).join(", ")}</span>
                      ) : null}
                    </div>

                    {typeof m.progress === "number" && m.progress > 0 ? (
                      <div className="mt-2 h-1.5 w-full max-w-xs bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${meta.bar} rounded-full`}
                          style={{ width: `${Math.min(100, Math.max(0, m.progress))}%` }}
                        />
                      </div>
                    ) : null}
                  </div>

                  {/* 우측: 상태 변경 */}
                  <div className="lg:w-48 shrink-0">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">상태 변경</label>
                    <select
                      value={m.status}
                      disabled={savingId === m._id}
                      onChange={(e) => handleStatusChange(m, e.target.value as MilestoneStatus)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      {NEXT_STATUS.map((s) => (
                        <option key={s} value={s}>
                          {STATUS_META[s].label}
                        </option>
                      ))}
                      {m.status === "archive" ? (
                        <option value="archive">{STATUS_META.archive.label}</option>
                      ) : null}
                    </select>
                    {savingId === m._id ? (
                      <p className="mt-1 text-xs text-orange-600">변경 중...</p>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <p className="mt-4 text-xs text-gray-500">
        * 담당자로 지정된 마일스톤만 표시되며, 상태 변경과 세부 To do 체크는 즉시 저장됩니다.
      </p>
    </div>
  )
}
